import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { qboFetchSearchItemsAsync, resetState } from '@features/qboSearchItems/qboSearchItemsSlice';
import { pascalToTitleCase } from '@utilities';
import { useSearchDelay } from '@hooks';
import BaseSetting from '@config/BaseSetting';
import { trim } from '@utilities/stringUtils';

const keyGroupBy = 'displayObjectName';
const keyLabelOption = 'itemName';

const useSearchItemSuggestion = (onSelectItem, initialFetch = false) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const keywordParams = searchParams.get('keyword');
  const { isApplicationReady } = useSelector((state) => state.application);
  const [searchKeyword, setSearchKeyword] = useState(keywordParams || '');
  const [loading, setLoading] = useState(false);
  const { QboSearchItems, fetchingQboSearchItems } = useSelector((state) => state.qboSearchItems);
  const hasFetchedInitialSearch = useRef(false);

  const qboFetchSearchItems = (q) => {
    if (!hasFetchedInitialSearch.current) {
      hasFetchedInitialSearch.current = true;
    }
    dispatch(
      qboFetchSearchItemsAsync({
        qboRealmId: id,
        top: BaseSetting.suggestionSearchLimit,
        query: trim(searchKeyword || q),
      })
    );
  };

  const suggestions = QboSearchItems.value.map((v) => {
    return {
      ...v,
      [keyGroupBy]: pascalToTitleCase(v.objectName).toUpperCase(),
    };
  });

  const selectItem = (item) => {
    if (onSelectItem && item)
      onSelectItem(item.lastVersionId, item.objectName, item.maskedId, false, true);
  };

  useSearchDelay(
    {
      actionFunc: () => {
        if (isApplicationReady) {
          qboFetchSearchItems(searchKeyword);
        }
      },
      resetFunc: () => {
        if (isApplicationReady) {
          qboFetchSearchItems();
        }
      },
    },
    searchKeyword,
    [isApplicationReady]
  );

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    if (!fetchingQboSearchItems && loading) {
      setLoading(false);
    }
  }, [fetchingQboSearchItems]);

  useEffect(() => {
    if (!loading && searchKeyword) {
      setLoading(true);
    }
  }, [searchKeyword]);

  // initial fetch
  useEffect(() => {
    if (
      initialFetch &&
      isApplicationReady &&
      trim(searchKeyword)?.length > BaseSetting.allowTypeRequestLength &&
      !hasFetchedInitialSearch.current
    ) {
      qboFetchSearchItems();
    }
  }, [searchKeyword]);

  const onSetSearchKeyword = (q) => {
    setSearchKeyword(q);
  };

  return {
    QboSearchItems,
    loadingSuggestion: loading,
    qboRealmId: id,
    searchKeyword,
    keyGroupBy,
    keyLabelOption,
    suggestions,
    onSelectItem: selectItem,
    setSearchKeyword: onSetSearchKeyword,
  };
};

export default useSearchItemSuggestion;
